import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';
import type { Content } from '@plone/types';

const getApiBase = (): string => {
  let api = String((config.settings as any)?.apiPath || '').trim();

  if (!api) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/++api++`;
    }
    return '/++api++';
  }

  if (api.includes('/++api++')) return api;

  return api.replace(/\/+$/, '') + '/++api++';
};

const ensureLeadingSlash = (path: string): string => {
  return path.startsWith('/') ? path : `/${path}`;
};

const buildInlinePdfUrl = (downloadUrl: string): string => {
  const base = getApiBase();
  const u = new URL(
    downloadUrl,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
  );
  const sitePath = u.pathname.replace(/^\/\+\+api\+\+/, '');
  const baseDoc = sitePath.split('/@@download/')[0];
  return `${base}${ensureLeadingSlash(baseDoc)}/@@inline-file/file`;
};

const DownloadRedirectView = ({
  content,
}: {
  content: Partial<Content> & any;
}) => {
  const prev = useSelector((s: any) => s.prevRoute?.prev);

  useEffect(() => {
    if (typeof window === 'undefined' || !content) return;
    if (!('location' in window) || !window.location) return;

    const cameFromAdd =
      !!prev &&
      prev.pathname.endsWith('/add') &&
      new URLSearchParams(prev.search).has('type') &&
      /^(File|Image)$/.test(new URLSearchParams(prev.search).get('type') || '');

    if (
      cameFromAdd &&
      (content['@type'] === 'File' || content['@type'] === 'Image')
    ) {
      const appUrl = flattenToAppURL(content['@id'] || '');
      const url = new URL(appUrl, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      parts.pop();
      if (typeof window.location.replace === 'function') {
        window.location.replace(`/${parts.join('/')}/contents`);
      }
      return;
    }

    const file = content?.file;
    const image = content?.image;

    let targetUrl: string | null = null;

    if (file?.download) {
      if (file['content-type'] === 'application/pdf') {
        targetUrl = buildInlinePdfUrl(file.download);
      } else {
        targetUrl = flattenToAppURL(file.download);
      }
    } else if (image?.download) {
      targetUrl = flattenToAppURL(image.download);
    }

    if (!targetUrl) return;

    if (typeof window.location.replace === 'function') {
      window.location.replace(targetUrl);
    } else if (typeof window.location.assign === 'function') {
      window.location.assign(targetUrl);
    }
  }, [content, prev]);

  return null;
};

export default DownloadRedirectView;
